import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Upload, Trash2, Image as ImageIcon, Loader2, Copy, Check, FileText, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Images = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [catalogUrl, setCatalogUrl] = useState('');
  const fileInputRef = useRef(null);
  const catalogInputRef = useRef(null);

  useEffect(() => {
    loadImages();
    loadCatalogUrl();
  }, []);

  const loadImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/uploads/images`, {
        headers: getAuthHeaders()
      });
      setImages(response.data);
    } catch (error) {
      console.error('Erro ao carregar imagens:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogUrl = async () => {
    try {
      const resp = await axios.get(`${API}/catalogo/url`);
      setCatalogUrl(resp.data.url || '');
    } catch (err) {
      console.error('Erro ao carregar catálogo:', err);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({ title: 'Erro', description: 'Apenas imagens JPG, PNG, GIF ou WEBP são permitidas', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Erro', description: 'A imagem deve ter no máximo 5MB', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/uploads/image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Sucesso!', description: 'Imagem enviada com sucesso' });
      loadImages();
    } catch (error) {
      toast({ title: 'Erro', description: error.response?.data?.detail || 'Erro ao fazer upload', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCatalogUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'Erro', description: 'O catálogo deve ser um ficheiro PDF', variant: 'destructive' });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'Erro', description: 'O ficheiro deve ter no máximo 50MB', variant: 'destructive' });
      return;
    }

    setUploadingCatalog(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/admin/uploads/catalog`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Catálogo Atualizado!', description: 'O novo catálogo já está disponível no site.' });
      setCatalogUrl(resp.data.url);
    } catch (error) {
      toast({ title: 'Erro', description: error.response?.data?.detail || 'Erro ao enviar catálogo', variant: 'destructive' });
    } finally {
      setUploadingCatalog(false);
      if (catalogInputRef.current) catalogInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta imagem?')) return;
    try {
      await axios.delete(`${API}/admin/uploads/image/${imageId}`, { headers: getAuthHeaders() });
      toast({ title: 'Sucesso!', description: 'Imagem eliminada com sucesso' });
      loadImages();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao eliminar imagem', variant: 'destructive' });
    }
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const copyToClipboard = async (url, id) => {
    try {
      const fullUrl = url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(id);
      toast({ title: 'URL copiada!', description: 'Cole a URL no campo de imagem desejado' });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast({ title: 'Erro', description: 'Não foi possível copiar a URL', variant: 'destructive' });
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Biblioteca de Media</h1>
          <p className="text-white/60">Gerencie imagens e catálogo do site</p>
        </div>
        <label className="cursor-pointer">
          <div className={`inline-flex items-center justify-center rounded-md text-sm font-bold transition-colors bg-[#cc8b3a] hover:bg-[#d4af37] text-black h-10 px-4 py-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />Enviar Imagem</>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleUpload} disabled={uploading} className="hidden" />
        </label>
      </div>

      {/* Catálogo PDF Section */}
      <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 mb-8" data-testid="catalog-section">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-lg bg-[#cc8b3a]/20 flex items-center justify-center">
                <FileText className="w-7 h-7 text-[#cc8b3a]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Catálogo de Produtos (PDF)</h2>
                <p className="text-white/50 text-sm">
                  {catalogUrl ? 'Catálogo ativo - clique para substituir' : 'Nenhum catálogo configurado'}
                </p>
                {catalogUrl && (
                  <a
                    href={catalogUrl.startsWith('/') ? `${BACKEND_URL}${catalogUrl}` : catalogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#cc8b3a] text-xs hover:underline mt-1 inline-block"
                  >
                    Ver catálogo atual
                  </a>
                )}
              </div>
            </div>
            <label className="cursor-pointer">
              <div className={`inline-flex items-center justify-center rounded-md text-sm font-bold transition-colors bg-[#cc8b3a] hover:bg-[#d4af37] text-black h-10 px-4 py-2 ${uploadingCatalog ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploadingCatalog ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" />{catalogUrl ? 'Substituir PDF' : 'Enviar PDF'}</>
                )}
              </div>
              <input ref={catalogInputRef} type="file" accept=".pdf,application/pdf" onChange={handleCatalogUpload} disabled={uploadingCatalog} className="hidden" data-testid="catalog-upload-input" />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Info box */}
      <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 mb-8">
        <CardContent className="p-4">
          <p className="text-white/80 text-sm">
            <strong className="text-[#cc8b3a]">Dica:</strong> Para trocar a imagem de um produto ou parceiro, edite-o e use o botão de upload no formulário. Também pode enviar imagens aqui e copiar a URL.
          </p>
        </CardContent>
      </Card>

      {/* Grid de Imagens */}
      {loading ? (
        <div className="text-center py-20">
          <Loader2 className="w-12 h-12 mx-auto text-[#cc8b3a] animate-spin mb-4" />
          <p className="text-white/60">A carregar imagens...</p>
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 mx-auto text-white/30 mb-4" />
          <p className="text-white/60 text-lg">Nenhuma imagem enviada</p>
          <p className="text-white/40 text-sm">Clique em "Enviar Imagem" para começar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map((image) => (
            <Card key={image.id} className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-video overflow-hidden bg-black/30">
                  <img src={getImageSrc(image.url)} alt={image.original_name || image.filename} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <p className="text-white text-sm mb-1 truncate" title={image.original_name || image.filename}>
                    {image.original_name || image.filename}
                  </p>
                  <p className="text-white/50 text-xs mb-3">{formatSize(image.size)}</p>
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => copyToClipboard(image.url, image.id)} className="flex-1 bg-[#cc8b3a] hover:bg-[#d4af37] text-black">
                      {copiedId === image.id ? (<><Check className="w-4 h-4 mr-1" />Copiado!</>) : (<><Copy className="w-4 h-4 mr-1" />Copiar URL</>)}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(image.id)} className="border-red-500/30 text-red-500 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Images;
